/**
 * Enums métier MiniSOC.
 *
 * Ces valeurs sont dupliquées à l'identique dans le schéma Prisma (enums
 * PostgreSQL). Toute modification ici impose une migration côté API.
 */
export const AlertSeverity = {
    CRITICAL: 'CRITICAL',
    HIGH: 'HIGH',
    MEDIUM: 'MEDIUM',
    LOW: 'LOW',
};
/** Ordre décroissant de gravité, utilisé pour les tris et les seuils. */
export const ALERT_SEVERITY_ORDER = [
    AlertSeverity.CRITICAL,
    AlertSeverity.HIGH,
    AlertSeverity.MEDIUM,
    AlertSeverity.LOW,
];
/** Libellés FR affichés dans le dashboard client et les emails. */
export const ALERT_SEVERITY_LABEL = {
    CRITICAL: 'Critique',
    HIGH: 'Élevée',
    MEDIUM: 'Moyenne',
    LOW: 'Faible',
};
/**
 * Cycle de vie d'une alerte.
 *
 * `RESOLVED` est l'état terminal ; la qualification vrai/faux positif est
 * portée séparément par `AlertResolution` afin de pouvoir compter les faux
 * positifs sans perdre l'information de clôture.
 */
export const AlertStatus = {
    OPEN: 'OPEN',
    IN_PROGRESS: 'IN_PROGRESS',
    RESOLVED: 'RESOLVED',
};
export const ALERT_STATUS_LABEL = {
    OPEN: 'Ouverte',
    IN_PROGRESS: 'En cours',
    RESOLVED: 'Résolue',
};
export const AlertResolution = {
    TRUE_POSITIVE: 'TRUE_POSITIVE',
    FALSE_POSITIVE: 'FALSE_POSITIVE',
    INCONCLUSIVE: 'INCONCLUSIVE',
};
export const ALERT_RESOLUTION_LABEL = {
    TRUE_POSITIVE: 'Vrai positif',
    FALSE_POSITIVE: 'Faux positif',
    INCONCLUSIVE: 'Non concluant',
};
export const TenantPlan = {
    ESSENTIEL: 'ESSENTIEL',
    PRO: 'PRO',
    ENTREPRISE: 'ENTREPRISE',
};
export const TenantStatus = {
    ONBOARDING: 'ONBOARDING',
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
};
export const TENANT_STATUS_LABEL = {
    ONBOARDING: 'En cours de déploiement',
    ACTIVE: 'Actif',
    SUSPENDED: 'Suspendu',
};
/** État d'un nœud Proxmox client, alimenté par les heartbeats et Zabbix. */
export const ApplianceStatus = {
    PROVISIONING: 'PROVISIONING',
    ONLINE: 'ONLINE',
    OFFLINE: 'OFFLINE',
    MAINTENANCE: 'MAINTENANCE',
};
export const APPLIANCE_STATUS_LABEL = {
    PROVISIONING: 'Provisioning',
    ONLINE: 'En ligne',
    OFFLINE: 'Hors ligne',
    MAINTENANCE: 'Maintenance',
};
/**
 * Où en est l'enrôlement d'un nœud : le jeton est émis à la déclaration et
 * consommé par le nœud lui-même, qui reçoit alors son secret HMAC.
 */
export const ApplianceEnrollmentState = {
    /** Aucun jeton émis et jamais enrôlé (nœud créé avant la fonctionnalité). */
    NONE: 'NONE',
    /** Jeton émis, en attente de l'appel du nœud. */
    PENDING: 'PENDING',
    /** Jeton émis mais périmé : en générer un nouveau. */
    EXPIRED: 'EXPIRED',
    /** Le nœud détient son secret. */
    ENROLLED: 'ENROLLED',
};
export const APPLIANCE_ENROLLMENT_LABEL = {
    NONE: 'Non enrôlé',
    PENDING: 'Jeton en attente',
    EXPIRED: 'Jeton expiré',
    ENROLLED: 'Enrôlé',
};
/**
 * Rôles utilisateurs.
 *
 * Les rôles `SOC_*` accèdent au backoffice et voient l'ensemble du parc.
 * Les rôles `CLIENT_*` sont rattachés à un tenant unique et n'accèdent qu'au
 * dashboard client, sous contrainte Row-Level Security.
 */
export const UserRole = {
    SOC_ADMIN: 'SOC_ADMIN',
    SOC_ANALYST: 'SOC_ANALYST',
    CLIENT_ADMIN: 'CLIENT_ADMIN',
    CLIENT_VIEWER: 'CLIENT_VIEWER',
};
export const SOC_ROLES = [UserRole.SOC_ADMIN, UserRole.SOC_ANALYST];
export const CLIENT_ROLES = [UserRole.CLIENT_ADMIN, UserRole.CLIENT_VIEWER];
export const isSocRole = (role) => SOC_ROLES.includes(role);
export const isClientRole = (role) => CLIENT_ROLES.includes(role);
/** Canaux de notification supportés (§7 du cahier des charges). */
export const NotificationChannel = {
    EMAIL: 'EMAIL',
    WEBSOCKET: 'WEBSOCKET',
};
export const NotificationStatus = {
    PENDING: 'PENDING',
    SENT: 'SENT',
    THROTTLED: 'THROTTLED',
    FAILED: 'FAILED',
};
/** Systèmes d'exploitation des endpoints surveillés (§3.1.4). */
export const EndpointPlatform = {
    WINDOWS: 'WINDOWS',
    LINUX: 'LINUX',
    MACOS: 'MACOS',
    OTHER: 'OTHER',
};
export const ENDPOINT_PLATFORM_LABEL = {
    WINDOWS: 'Windows',
    LINUX: 'Linux',
    MACOS: 'macOS',
    OTHER: 'Autre',
};
/**
 * Traduit le niveau de règle Wazuh (0-16) en sévérité MiniSOC.
 *
 * Les seuils suivent le catalogue Red Flag §5.3.2 : level 14+ = critique,
 * level 12-13 = élevée. En dessous de 12 une alerte n'est en principe pas
 * forwardée par le nœud, mais on reste défensif : un nœud mal configuré ne
 * doit pas faire échouer l'ingestion.
 */
export function severityFromWazuhLevel(level) {
    if (level >= 14)
        return AlertSeverity.CRITICAL;
    if (level >= 12)
        return AlertSeverity.HIGH;
    if (level >= 7)
        return AlertSeverity.MEDIUM;
    return AlertSeverity.LOW;
}
//# sourceMappingURL=enums.js.map